/**
 * Audit Log Hooks
 *
 * Fetches audit log data for superadmin or org admin views.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

// =============================================================================
// Types
// =============================================================================

export type AuditLogEntry = {
  id: string;
  source: "content" | "security";
  userId: string | null;
  userEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  changes: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
  orgId?: string | null;
  orgName?: string | null;
};

export type AuditLogResponse = {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  pageCount: number;
};

export type AuditLogParams = {
  orgId?: string; // If provided = org view, else superadmin global
  page: number;
  limit?: number;
  entityType?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  source?: "content" | "security" | "all";
};

// =============================================================================
// Hooks
// =============================================================================

const STALE_TIME = 30 * 1000; // 30 seconds

/**
 * Fetch audit log data.
 * If orgId is provided, fetches org-scoped logs.
 * Otherwise fetches global logs (superadmin only).
 */
export function useAuditLog(params: AuditLogParams) {
  const { orgId, page, limit = 50, entityType, action, startDate, endDate, source = "all" } = params;

  return useQuery({
    queryKey: ["audit-log", { orgId, page, limit, entityType, action, startDate, endDate, source }],
    queryFn: async () => {
      const query = {
        page: String(page),
        limit: String(limit),
        source,
        ...(entityType && { entityType }),
        ...(action && { action }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      };

      if (orgId) {
        // Org-scoped
        const res = await api["audit-log"].orgs[":orgId"].$get({
          param: { orgId },
          query,
        });
        if (!res.ok) {
          const error = (await res.json()) as { error?: string };
          throw new Error(error.error ?? "Failed to fetch audit log");
        }
        return res.json() as Promise<AuditLogResponse>;
      } else {
        // Global (superadmin)
        const res = await api["audit-log"].$get({ query });
        if (!res.ok) {
          const error = (await res.json()) as { error?: string };
          throw new Error(error.error ?? "Failed to fetch audit log");
        }
        return res.json() as Promise<AuditLogResponse>;
      }
    },
    staleTime: STALE_TIME,
  });
}

/**
 * Get export URL for org audit log CSV
 */
export function getAuditLogExportUrl(orgId: string): string {
  return `/api/audit-log/orgs/${orgId}/export`;
}
